// Hover-help copy for sidebar nav items (parents and children), keyed by the
// exact `name` string the role config / backend rights payload uses for that
// item — see src/assets/roleModel/superadmin.json and the STANDARD-role
// `getUsersRights` response for the authoritative list. Unknown names (a
// role variant we haven't catalogued) fall back to a generic-but-still
// useful description built from the name itself, so a hover never shows
// nothing.
export interface VwNavHelp {
  title: string;
  body: string;
}

const NAV_HELP: { [name: string]: string } = {
  'Dashboard': 'Live headcounts, KPI cards and activity charts for your property.',

  // Administrator
  'Adminstrator': 'User accounts, groups, devices, gates and property-wide configuration.',
  'User Group Details': 'Define user groups and the permission set each one grants.',
  'User Details': 'View, add and manage the user accounts on this property.',
  'Registration Codes': 'Generate and track self-registration codes issued to visitors.',
  'Registration Requests': 'Review and approve/reject pending self-registration requests.',
  'Device Binding Management': 'See which mobile devices are bound to this account.',
  'Mobile Binding License': 'Manage the mobile-binding license limit for this account.',
  'Configuration Setting': 'Turn account-level features on or off for this property.',
  'Property Hierarchy': 'Model the property’s levels, blocks and units.',
  'Hierarchy Setting': 'Configure the naming and structure of the property hierarchy.',
  'Gate Mapping': 'Map physical gates to entry points for check-in/out routing.',
  'Entry Point Mapping': 'Assign entry points to the devices and gates that serve them.',
  'Reset Password Request': 'Review pending password-reset requests from users.',
  'System Notification': 'Compose and send system-wide announcements to users.',
  'OCR API Key': 'Manage the API key used for document/plate OCR scanning.',
  'OCR Key Stats': 'Usage stats for the OCR API key on this account.',
  'Superowner Request Details': 'Requests awaiting the platform super-owner’s review.',

  // Property management
  'Property Management': 'Property records, plans and ownership details.',
  'Premise Management': 'Manage premises registered under this property.',
  'Premise Details': 'View and edit an individual premise’s details.',
  'Property Registration Details': 'How this property was registered and its current status.',
  'Property Owner Stock Manager': 'Track stock/inventory assigned to property owners.',
  'Plan Details': 'Subscription plan details for this account.',

  // Visitors / Guests
  'Visitor': 'Everyone who has visited this property, current and past.',
  'Guests': 'Everyone who has visited this property, current and past.',
  'Add Guest': 'Register a new booked or walk-in guest.',
  'Add Visitor': 'Register a new booked or walk-in visitor.',
  'Add Guest/Service Provider': 'Register a new guest or service provider visit.',
  'Guest Details': 'Full guest list with check-in/out history and search.',
  'Visitor Details': 'Full visitor list with check-in/out history and search.',
  'Current Guest List': 'Guests who are currently checked in.',
  'Current Visitor List': 'Visitors who are currently checked in.',
  'Current Guest Details': 'Detail view of guests currently on the property.',
  'Current Visitor Details': 'Detail view of visitors currently on the property.',
  'Booked Guest': 'Guests booked but not yet checked in.',
  'Booked Visitor': 'Visitors booked but not yet checked in.',
  'Booked Guest List': 'Upcoming guests booked ahead of their visit.',
  'Booked Visitor List': 'Upcoming visitors booked ahead of their visit.',
  'Recurrent Guest Details': 'Guests with a recurring, repeat-visit schedule.',
  'Recurrent Visitor Details': 'Visitors with a recurring, repeat-visit schedule.',
  'Guest History': 'Past guest visits with check-in/out timestamps.',
  'Visitor History': 'Past visitor visits with check-in/out timestamps.',
  'Trespasser Guest': 'Guests flagged for entering without a valid check-in.',
  'Trespasser Visitor': 'Visitors flagged for entering without a valid check-in.',
  'Self Guest Details': 'Guests who self-registered via QR code or kiosk.',
  'Self Check In QR': 'Generate the self-check-in QR code for this property.',
  'Self Service Link': 'Shareable self-service link for guest self-registration.',
  'Self Service List': 'Guests who registered through a self-service link.',

  // Service provider
  'Service Provider': 'Contractors and vendors who service this property.',
  'Contractors': 'Contractors and vendors who service this property.',
  'Add Contractor': 'Register a new booked or walk-in contractor.',
  'Add Service Provider': 'Register a new booked or walk-in service provider.',
  'Service Provider Details': 'Full service-provider list with visit history.',
  'Contractor Details': 'Full contractor list with visit history.',
  'Booked Contractor': 'Contractors booked but not yet checked in.',
  'Booked Service Provider': 'Service providers booked but not yet checked in.',
  'Booked Service Provider List': 'Service providers booked but not yet checked in.',
  'Current Service Provider List': 'Service providers currently on the property.',
  'Recurrent Service Provider': 'Service providers with a recurring visit schedule.',
  'Service Provider History': 'Past service-provider visits and check-out times.',
  'Trespasser Service Provider': 'Service providers flagged for entering without a valid check-in.',

  // Staff / Office staff
  'Staff': 'Employees and office staff registered on this property.',
  'Staff Details': 'Full staff list with attendance and check-in history.',
  'Current Staff': 'Staff currently checked in.',
  'Scheduled Staff': 'Staff scheduled but not yet checked in today.',
  'Staff History': 'Past staff check-in/out records.',

  // Domestic staff / House help
  'Domestic Staff': 'Household staff (domestic workers) registered to residents.',
  'Domestic Staff Details': 'Full domestic-staff list with visit history.',
  'Current Domestic Staff': 'Domestic staff currently checked in.',
  'Scheduled Domestic Staff': 'Domestic staff scheduled but not yet checked in.',
  'Domestic Staff History': 'Past domestic-staff check-in/out records.',
  'House Help': 'Household help registered to residents on this property.',
  'House Help Details': 'Full house-help list with visit history.',
  'House Helps Detail': 'Full house-help list with visit history.',
  'House Helps Details': 'Full house-help list with visit history.',
  'Register House Help': 'Register a new house-help worker.',
  'Register House Helps': 'Register a new house-help worker.',
  'House Help History': 'Past house-help check-in/out records.',
  'Trespasser House Help': 'House help flagged for entering without a valid check-in.',

  // Resident / Family
  'Residents': 'Residents living on this property.',
  'Resident Details': 'Full resident list with unit and contact details.',
  'Current Resident': 'Residents currently checked in on-site.',
  'Scheduled Resident': 'Residents scheduled but not yet checked in.',
  'Resident History': 'Past resident check-in/out records.',
  'Family Management': 'Family members linked to a resident’s account.',
  'Family Details': 'Family member list and their access details.',

  // Vehicle
  'Vehicle Management': 'Vehicles registered to residents, staff and visitors.',
  'Vehicle List': 'All registered vehicles and their assigned owners.',
  'Current Vehicle': 'Vehicles currently on the property.',
  'Booked Vehicle': 'Vehicles booked but not yet checked in.',
  'Vehicle History': 'Past vehicle check-in/out records.',

  // Flagged / Blacklist / Rejected
  'Flagged Visitor': 'Visitors flagged for review before they can check in again.',
  'Flagged Visitor Details': 'Full list of flagged visitors and the reason for each flag.',
  'Unflagged Visitor Details': 'Visitors who were flagged and have since been cleared.',
  'Blacked List Visitor': 'Visitors barred from entering this property.',
  'BlackList Visitor Details': 'Full list of blacklisted visitors and the reason for each entry.',
  'BlackList Visitor History': 'Past blacklist actions, including removals.',
  'Rejected Visitor': 'Visit requests that were declined.',
  'Rejected Visitor Details': 'Full list of rejected visit requests and the reason given.',

  // Reports / Notifications
  'Report': 'Exited visitor/vehicle reports and audit trails for this property.',
  'Audit Report': 'A chronological log of actions taken across the system.',
  'Daily Guest Report': 'A daily summary of guest activity, sent by email.',
  'Exited Vistor Report': 'Visitors who checked out, filterable by date and property.',
  'Visitor Vehicle Report': 'Vehicle exits, filterable by date and property.',
  'Notifications': 'System and panic notifications sent to this account.',
  'Notification Details': 'Full notification history for this account.',
  'Panic Notifications': 'Panic-button alerts raised on this property, with live counts.',
};

export function getNavHelp(name: string): VwNavHelp {
  const body = NAV_HELP[name];
  return {
    title: name,
    body: body || `Open ${name} to view and manage its records.`,
  };
}
